import React from 'react';
import { StatusPill } from './EntityCard';
import { Button } from './Button';
import { TripStrip } from './TripStrip';
import { moveChipLabel, moveStatusMeta, jobTypeShort, MOVE_TERMINAL, moveDatesSummary } from '../../lib/moves';

// ============================================================================
// Move header pieces (Move Dates Showroom pick 3B, Jaylen 2026-09-18).
//
//   <MoveSwitcher moves current onSelect onNew />   chip strip under the
//        client-file header: ● Load Sep 16 · Out-of-State  ● Oct 3 · Delivery
//        + New move. One-move clients see one chip.
//   <MoveHeaderCard move onChange onEdit … />        the selected move's block:
//        the trip strip, origin → destination (+ stops), job type, one Edit.
// ============================================================================

export function MoveStatusPill({ status, className = '' }) {
    const m = moveStatusMeta(status);
    return <StatusPill label={m.label} color={m.color} className={className} />;
}

export function MoveSwitcher({ moves = [], currentId, onSelect, onNew, newMenu = null, className = '' }) {
    const list = [...moves].sort((a, b) => {
        const ta = MOVE_TERMINAL.has(a.status) ? 1 : 0, tb = MOVE_TERMINAL.has(b.status) ? 1 : 0;
        if (ta !== tb) return ta - tb;
        return String(a.load_date || '9999').localeCompare(String(b.load_date || '9999'));
    });
    return (
        <div className={`flex items-center gap-1.5 flex-wrap ${className}`} role="tablist" aria-label="Moves">
            {list.map(m => {
                const on = m.id === currentId;
                const meta = moveStatusMeta(m.status);
                return (
                    <button key={m.id} type="button" role="tab" aria-selected={on} onClick={() => onSelect?.(m)}
                        className={`h-8 px-2.5 rounded-lg border text-[12.5px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'} ${MOVE_TERMINAL.has(m.status) && !on ? 'opacity-70' : ''}`}
                        title={`${moveDatesSummary(m)} · ${meta.label}`}>
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                        {moveChipLabel(m)}
                    </button>
                );
            })}
            {onNew && (newMenu || (
                <button type="button" onClick={onNew} className="h-8 px-2.5 rounded-lg border border-dashed border-slate-300 text-[12.5px] font-bold text-slate-500 hover:text-slate-800 hover:border-slate-500">+ New move</button>
            ))}
        </div>
    );
}

// The block. `onChange(patch)` is the TripStrip's change handler (dates edit
// in place); `onEdit` opens the addresses / type / access editor.
export function MoveHeaderCard({ move, onChange, onEdit, disabled = false, className = '', title, extra, compact = false }) {
    if (!move) return null;
    const stops = (() => {
        const a = move.additional_stops; if (!a) return [];
        if (Array.isArray(a)) return a.filter(Boolean);
        try { const p = JSON.parse(a); if (Array.isArray(p)) return p.filter(Boolean); } catch { /* text */ }
        return String(a).split('\n').map(s => s.trim()).filter(Boolean);
    })();
    return (
        <div className={`rounded-lg border border-slate-200 bg-white ${compact ? 'p-3' : 'p-4'} ${className}`}>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{title || `Move ${move.move_number ? move.move_number.replace(/^BBM-\d{4}-/, '') : ''}`}</span>
                    <span className="text-[12px] font-bold text-slate-700">{jobTypeShort(move.job_type)}</span>
                    <MoveStatusPill status={move.status} />
                    {move.storage_mode && <StatusPill label={move.storage_mode === 'included' ? 'Storage included' : 'Storage · split'} color="#64748b" />}
                </div>
                <div className="flex items-center gap-2">
                    {extra}
                    {onEdit && !disabled && <Button size="sm" variant="secondary" onClick={onEdit}>Edit</Button>}
                </div>
            </div>
            <TripStrip value={move} onChange={onChange} disabled={disabled} showLabel={false} size={compact ? 'sm' : 'md'} className="border-0 p-0" />
            <div className="mt-3 grid gap-1.5 text-[13px]">
                <div className="flex gap-2 min-w-0"><span className="w-10 flex-shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 pt-0.5">From</span><span className={`min-w-0 truncate ${move.origin_address ? 'text-slate-800' : 'text-slate-400'}`}>{move.origin_address || 'No origin yet'}</span></div>
                {stops.map((s, i) => <div key={i} className="flex gap-2 min-w-0"><span className="w-10 flex-shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 pt-0.5">Stop {i + 1}</span><span className="min-w-0 truncate text-slate-800">{typeof s === 'string' ? s : (s.address || '')}</span></div>)}
                <div className="flex gap-2 min-w-0"><span className="w-10 flex-shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 pt-0.5">To</span><span className={`min-w-0 truncate ${move.destination_address ? 'text-slate-800' : 'text-slate-400'}`}>{move.destination_address || (move.job_type === 'LABOR_ONLY' || move.job_type === 'DUMP_RUN' ? '—' : 'No destination yet')}</span></div>
            </div>
        </div>
    );
}

export default MoveHeaderCard;
